<script setup lang="ts">
interface Props {
  variant?: 'primary' | 'secondary' | 'danger' | 'google' | 'microsoft'
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  fullWidth?: boolean
  size?: 'small' | 'medium' | 'large'
  loading?: boolean
}

withDefaults(defineProps<Props>(), {
  variant: 'primary',
  type: 'button',
  disabled: false,
  fullWidth: false,
  size: 'medium',
  loading: false
})
</script>

<template>
  <button
    :type="type"
    :class="['btn', `btn--${variant}`, `btn--${size}`, { 'btn--full-width': fullWidth, 'btn--loading': loading }]"
    :disabled="disabled || loading"
  >
    <span v-if="loading" class="btn__spinner"></span>
    <slot />
  </button>
</template>

<style scoped>
/* Variant classes like .btn--primary are dynamically applied via :class binding */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-xl);
  border: none;
  border-radius: var(--radius-base);
  /* noinspection CssNoGenericFontName */
  font-family: var(--font-family-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-duration-fast) var(--transition-easing-ease-out);
  white-space: nowrap;
}

.btn:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

/* Disabled state - one muted grey treatment shared by every variant, so an
   inactive button never reads as an active one that is simply broken.
   Disabled controls are exempt from the WCAG 1.4.3 contrast minimum
   ("incidental: ...part of an inactive user interface component"), so the
   colours here deliberately sit below 4.5:1 - keeping full contrast is what
   made the state invisible.
   Loading is excluded: it also sets [disabled], but must read as in-progress
   with the spinner as the dominant signal. */
.btn:disabled {
  cursor: not-allowed;
}

.btn:disabled:not(.btn--loading) {
  background-color: var(--color-button-disabled);
  color: var(--color-button-disabled-text);
  border-color: var(--color-button-disabled-border);
}

.btn--full-width {
  width: 100%;
}

/* Primary button - Scrivio brand color */
.btn--primary {
  background-color: var(--color-button-primary);
  color: var(--color-button-primary-text);
}

.btn--primary:hover:not(:disabled) {
  background-color: var(--color-button-primary-hover);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn--primary:active:not(:disabled) {
  transform: translateY(0);
}

/* Secondary button - White with outline (subordinate to the filled primary) */
.btn--secondary {
  background-color: var(--color-background-content);
  color: var(--color-button-secondary-text);
  border: 1px solid var(--color-border-input);
}

.btn--secondary:hover:not(:disabled) {
  background-color: var(--color-background-canvas);
  border-color: var(--color-border-input-focus);
}

/* Danger button - Red outline for destructive actions (transparent fill) */
.btn--danger {
  background-color: transparent;
  color: var(--color-semantic-error);
  border: 1px solid var(--color-semantic-error);
}

.btn--danger:hover:not(:disabled) {
  /* Subtle red tint on hover; darken text/border for emphasis */
  background-color: var(--color-semantic-error-background-subtle);
  color: var(--color-button-danger-hover);
  border-color: var(--color-button-danger-hover);
}

/* Google button - White with border */
.btn--google {
  background-color: var(--color-background-content);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-default);
}

.btn--google:hover:not(:disabled) {
  background-color: var(--color-background-canvas);
  border-color: var(--color-border-input);
}

/* Microsoft button - White with border */
.btn--microsoft {
  background-color: var(--color-background-content);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-default);
}

.btn--microsoft:hover:not(:disabled) {
  background-color: var(--color-background-canvas);
  border-color: var(--color-border-input);
}

/* Size variants */
.btn--small {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
}

.btn--medium {
  /* Default size - already defined in base .btn styles */
  padding: var(--spacing-md) var(--spacing-xl);
  font-size: var(--font-size-base);
}

.btn--large {
  padding: var(--spacing-lg) var(--spacing-2xl);
  font-size: var(--font-size-lg);
}

/* Loading state */
.btn--loading {
  position: relative;
}

/* Loading also sets [disabled]; outrank the not-allowed cursor from there. */
.btn--loading:disabled {
  cursor: wait;
}

.btn--loading > :not(.btn__spinner) {
  opacity: 0;
}

.btn__spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 1em;
  height: 1em;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: btn-spinner-rotate 0.6s linear infinite;
}

@keyframes btn-spinner-rotate {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

/* Width as well as height, so an icon-only button is a whole touch target too. */
@media (--breakpoint-md-down) {
  .btn {
    min-width: var(--sizing-touch-target-min);
    min-height: var(--sizing-touch-target-min);
  }
}
</style>
